import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { FolderKanban, FileText, ClipboardList, FolderOpen, User as UserIcon } from "lucide-react";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState(null);
  const [localQ, setLocalQ] = useState(q);

  useEffect(() => {
    if (!q) return;
    api.get("/search", { params: { q } }).then(({ data }) => setResults(data));
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    setParams({ q: localQ });
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <div className="label-mono">UNIVERSAL SEARCH</div>
        <h1 className="font-heading text-4xl font-black tracking-tight">Search</h1>
      </div>

      <form onSubmit={submit} className="brut-card p-3">
        <input
          value={localQ}
          onChange={(e) => setLocalQ(e.target.value)}
          placeholder="Search across jobs, files, daily entries, invoices, people…"
          className="w-full brut-input px-3 py-2 text-sm font-mono"
          autoFocus
          data-testid="search-page-input"
        />
      </form>

      {q && results && (
        <div className="space-y-6">
          <Section title="Jobs" icon={FolderKanban} items={results.jobs} render={(j) => (
            <div className="flex items-center justify-between" key={j.id}>
              <div>
                <div className="font-mono text-xs text-zinc-500">{j.job_number}</div>
                <Link to="/jobs" className="font-bold hover:underline">{j.project_name}</Link>
                <div className="text-xs text-zinc-600">{j.client}</div>
              </div>
              <span className="label-mono">{j.stage}</span>
            </div>
          )} />

          <Section title="Daily Entries" icon={ClipboardList} items={results.daily_entries} render={(d) => (
            <div key={d.id} className="flex justify-between text-sm">
              <div><span className="font-mono text-xs">{d.date}</span> · <b>{d.project_name}</b></div>
              <div className="font-mono text-xs">{d.user_name} · {d.efficiency}%</div>
            </div>
          )} />

          <Section title="Invoices" icon={FileText} items={results.invoices} render={(i) => (
            <div key={i.id} className="flex justify-between text-sm">
              <div className="font-mono text-xs">{i.invoice_number}</div>
              <div><b>{i.client_name}</b> · {i.status}</div>
            </div>
          )} />

          <Section title="Files" icon={FolderOpen} items={results.files} render={(f) => (
            <div key={f.id} className="text-sm">
              <b>{f.original_filename}</b>
              <span className="font-mono text-xs text-zinc-500 ml-2">{f.storage_path}</span>
            </div>
          )} />

          <Section title="People" icon={UserIcon} items={results.users} render={(u) => (
            <div key={u.user_id} className="text-sm">
              <b>{u.name}</b> · <span className="font-mono text-xs text-zinc-600">{u.email}</span>
            </div>
          )} />
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, items, render }) {
  return (
    <div className="brut-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" />
        <div className="label-mono">{title} · {items?.length || 0}</div>
      </div>
      {items?.length === 0 ? (
        <div className="text-xs font-mono text-zinc-400 py-2">— no matches —</div>
      ) : (
        <div className="space-y-2">
          {items?.map((item, idx) => <div key={idx} className="p-2 border-b border-zinc-100 last:border-0">{render(item)}</div>)}
        </div>
      )}
    </div>
  );
}
